import { useState } from "react";
import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { useIdeaCards, useCreateIdeaCard, useUpdateIdeaCard, useDeleteIdeaCard, useRestoreIdeaCard } from "../../../lib/presentationQueries";
import { useAutosaveEntity } from "../../../hooks/useAutosaveEntity";
import AutosaveField from "./AutosaveField";
import AutosaveStatus from "./AutosaveStatus";
import ConflictBanner from "./ConflictBanner";
import ConfirmDialog from "./ConfirmDialog";
import InlineFileAttachments from "../files/InlineFileAttachments";

function IdeaCardTile({ workspaceId, presId, card, onDeleteRequested }) {
  const updateIdeaCard = useUpdateIdeaCard(workspaceId, presId);
  const auto = useAutosaveEntity({
    initialValues: { title: card.title || "", body_text: card.body_text || "" },
    revision: card.revision,
    onSave: (payload) => updateIdeaCard.mutateAsync({ ideaId: card.idea_id, ...payload }),
  });
  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-4" data-testid={`idea-card-${card.idea_id}`}>
      <div className="flex items-center justify-between mb-2">
        <AutosaveStatus status={auto.status} onRetry={auto.retry} testId={`idea-card-${card.idea_id}-autosave-status`} />
        <button
          onClick={() => onDeleteRequested(card.idea_id)} className="text-ink-soft hover:text-terracotta"
          aria-label="Delete idea card" data-testid={`idea-card-${card.idea_id}-delete`}
        >
          <Trash2 size={14} />
        </button>
      </div>
      <ConflictBanner conflict={auto.conflict} onKeepMine={auto.keepMineAfterConflict} onUseServer={auto.useServerAfterConflict} />
      <AutosaveField
        value={auto.values.title} onChange={(v) => auto.setField("title", v)}
        placeholder="Idea title" testId={`idea-card-${card.idea_id}-title`}
      />
      <AutosaveField
        value={auto.values.body_text} onChange={(v) => auto.setField("body_text", v)}
        placeholder="Details…" multiline testId={`idea-card-${card.idea_id}-body`}
      />
      <div className="mt-2">
        <InlineFileAttachments
          workspaceId={workspaceId} targetType="idea_card" targetId={`${presId}:${card.idea_id}`}
          label={`the idea "${card.title || "Untitled"}"`} compact
        />
      </div>
    </div>
  );
}

export default function IdeaBankArea({ workspaceId, presId }) {
  const [showDeleted, setShowDeleted] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const { data } = useIdeaCards(workspaceId, presId, showDeleted);
  const createIdeaCard = useCreateIdeaCard(workspaceId, presId);
  const deleteIdeaCard = useDeleteIdeaCard(workspaceId, presId);
  const restoreIdeaCard = useRestoreIdeaCard(workspaceId, presId);
  const items = data?.items || [];
  const active = items.filter((c) => c.status === "active");
  const deleted = items.filter((c) => c.status === "deleted");

  return (
    <div data-testid="presentation-idea-bank-area">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-serif text-xl text-ink">Idea Bank</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowDeleted((s) => !s)} className="text-xs text-ink-soft hover:text-ink underline" data-testid="idea-bank-toggle-deleted">
            {showDeleted ? "Hide deleted" : "Show deleted"}
          </button>
          <button
            onClick={() => createIdeaCard.mutate({ title: "New idea", body_text: "" })}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:text-terracotta/80"
            data-testid="idea-bank-add-card"
          >
            <Plus size={15} /> Add idea
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="idea-bank-active-list">
        {active.map((card) => (
          <IdeaCardTile key={card.idea_id} workspaceId={workspaceId} presId={presId} card={card} onDeleteRequested={setPendingDeleteId} />
        ))}
        {active.length === 0 && <p className="text-ink-soft text-sm">No ideas yet — capture whatever comes to mind.</p>}
      </div>
      {showDeleted && deleted.length > 0 && (
        <div className="mt-6" data-testid="idea-bank-deleted-list">
          <p className="text-sm uppercase tracking-wide text-ink-soft mb-2">Deleted</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {deleted.map((card) => (
              <div key={card.idea_id} className="border border-ink/10 bg-white/30 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`idea-card-deleted-${card.idea_id}`}>
                <p className="text-sm text-ink-soft truncate">{card.title}</p>
                <button
                  onClick={() => restoreIdeaCard.mutate(card.idea_id)}
                  className="text-xs text-terracotta hover:text-terracotta/80 inline-flex items-center gap-1 shrink-0"
                  data-testid={`idea-card-${card.idea_id}-restore`}
                >
                  <RotateCcw size={12} /> Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={!!pendingDeleteId}
        onOpenChange={(o) => !o && setPendingDeleteId(null)}
        title="Delete this idea card?"
        description="It moves to Deleted and can be restored any time — nothing is lost."
        confirmLabel="Delete"
        onConfirm={() => { deleteIdeaCard.mutate(pendingDeleteId); setPendingDeleteId(null); }}
        testId="idea-card-delete-confirm"
      />
    </div>
  );
}
